import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { useBrowserCompatibility } from '@ctw/shared/hooks/use-browser-compatibility';
import { getApiServiceBaseUrl, getEnvFromCurrentHostname } from '@ctw/shared/utils/environments';
import { tw } from '@ctw/shared/utils/tailwind';
import { type PropsWithChildren, createContext, useContext, useEffect, useState } from 'react';
import ErrorMessage from '../ErrorMessage';

interface SmartAppContextProps {
  zusAccessToken?: string;
  ehrPatientId?: string;
  ehr?: string;
  ehrContext?: Record<string, string>;
}

const defaultContextValue: SmartAppContextProps = {};

export const SmartAppContext = createContext<SmartAppContextProps>(defaultContextValue);

export const SmartAppProvider = ({ children }: PropsWithChildren<object>) => {
  const { readableBrowserString } = useBrowserCompatibility();
  const [contextState, setContextState] = useState<SmartAppContextProps>({});
  const [loginError, setLoginError] = useState<boolean>(false);
  const env = getEnvFromCurrentHostname();
  const ehrHooksUrl = getApiServiceBaseUrl({ service: 'ehr-hooks', env });

  useEffect(() => {
    const fetchZusToken = async () => {
      try {
        const tokenResponse = await fetch(`${ehrHooksUrl}/smart-auth/context`, {
          credentials: 'include',
        });

        if (tokenResponse.status !== 200) {
          throw new Error('Could not get Zus token');
        }

        const data = await tokenResponse.json();
        setContextState({
          zusAccessToken: data.accessToken,
          ehrPatientId: data.patientId,
          ehr: data.ehr,
          ehrContext: data.ehrContext,
        });
      } catch (_error) {
        setLoginError(true);
        throw new Error('An error occurred while fetching the Zus token.');
      }
    };

    void fetchZusToken();
  }, [ehrHooksUrl]);

  if (!(contextState.zusAccessToken || loginError)) {
    return (
      <LoadingSpinner
        className={tw`fixed inset-0 flex h-screen items-center justify-center`}
        message="Logging in to Zus"
      />
    );
  }
  if (loginError) {
    return (
      <div className={tw`p-16`}>
        <ErrorMessage
          title="SMART on FHIR app failed to launch"
          message={`The ZAP failed to load, please close the application and try launching again. If the issue continues, it may be caused by your browser (${readableBrowserString}) blocking third-party cookies. Please check your browser settings and try again.`}
        />
      </div>
    );
  }

  return <SmartAppContext.Provider value={contextState}>{children}</SmartAppContext.Provider>;
};

export const useSmartApp = () => useContext(SmartAppContext);
